'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Check } from 'lucide-react';
import { Headline, Label, Reveal, GlassCard, PinkButton } from '../ui';

/**
 * Monthly GBP prices are confirmed. Billing runs through Clerk's built-in
 * billing (no pricing config in code).
 * TODO(pricing): the 20% annual discount is ASSUMED — confirm the real annual
 * rate before launch.
 */
const ANNUAL_DISCOUNT = 0.8; // TODO(pricing): confirm — assumed 20% off.

type Tier = {
  name: string;
  monthly?: number; // GBP per month; omit for the contact-sales tier
  lockPrice?: boolean; // price never changes with the annual toggle
  custom?: string; // big label when there's no numeric price
  customNote?: string;
  features: string[];
  cta: string;
  href: string;
  popular?: boolean;
  highlight?: boolean;
};

const tiers: Tier[] = [
  {
    name: 'Creator',
    monthly: 30,
    lockPrice: true, // £30 flat — no annual discount
    features: ['20 hours of footage', 'AI moment search', 'Smart clipping'],
    cta: 'Get Started',
    href: '/register',
  },
  {
    name: 'Pro',
    monthly: 69,
    features: [
      '50 hours of footage',
      'Priority AI search',
      'Custom MomentCards',
      'Batch export',
    ],
    cta: 'Go Pro',
    href: '/register',
    popular: true,
  },
  {
    name: 'Studio',
    monthly: 120,
    features: ['Unlimited archive', 'Full API access', 'Shared team libraries'],
    cta: 'Start Trial',
    href: '/register',
  },
  {
    name: 'Enterprise',
    custom: 'Talk',
    customNote: 'to sales for custom scale',
    features: ['White-label solution', 'SSO & enterprise security', 'Dedicated support'],
    cta: 'Talk to Sales',
    href: '/contact',
    highlight: true,
  },
];

export default function PricingSection() {
  const [annual, setAnnual] = useState(true);

  return (
    <section id="pricing" className="mx-auto max-w-[1280px] px-6 py-28">
      <Reveal className="mb-16 text-center">
        <Label className="mb-4">Pricing</Label>
        <Headline className="mb-8 text-4xl sm:text-5xl">
          Choose your power level
        </Headline>

        <div className="flex items-center justify-center gap-4">
          <span
            className={`font-mk-body ${!annual ? 'text-mk-ink' : 'text-mk-muted'}`}
          >
            Monthly
          </span>
          <button
            onClick={() => setAnnual((a) => !a)}
            aria-label="Toggle annual billing"
            className="relative flex h-8 w-14 items-center rounded-full bg-mk-s p-1"
          >
            <div
              className="h-6 w-6 rounded-full bg-mk-pink transition-transform"
              style={{ transform: annual ? 'translateX(24px)' : 'translateX(0px)' }}
            />
          </button>
          <span className="font-mk-body text-mk-ink">
            Annual{' '}
            {/* TODO(pricing): confirm discount */}
            <span className="ml-1 text-xs text-mk-pink">(Save 20%)</span>
          </span>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {tiers.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.06}>
            <GlassCard
              className={[
                'flex h-full flex-col p-8',
                t.popular ? 'relative border-mk-pink' : '',
                t.highlight ? 'bg-mk-s-highest' : '',
              ].join(' ')}
            >
              {t.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-mk-pink px-3 py-1 font-mk-label text-[10px] font-bold uppercase tracking-[0.18em] text-mk-on-pink">
                  Most Popular
                </div>
              )}
              <Headline as="h3" className="mb-2 text-2xl">
                {t.name}
              </Headline>
              <div className="mb-6">
                {typeof t.monthly === 'number' ? (
                  (() => {
                    const discounted = annual && !t.lockPrice;
                    return (
                      <>
                        <span className="font-mk-display text-5xl text-mk-ink">
                          £{discounted ? Math.round(t.monthly * ANNUAL_DISCOUNT) : t.monthly}
                        </span>
                        <span className="text-mk-muted">/mo</span>
                        {annual && (
                          <span className="block text-sm text-mk-muted">
                            billed annually
                          </span>
                        )}
                      </>
                    );
                  })()
                ) : (
                  <>
                    <span className="font-mk-display text-5xl text-mk-ink">
                      {t.custom}
                    </span>
                    <span className="block text-sm text-mk-muted">
                      {t.customNote}
                    </span>
                  </>
                )}
              </div>
              <ul className="mb-8 flex-1 space-y-4">
                {t.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-center gap-2 font-mk-body text-sm text-mk-muted"
                  >
                    <Check className="h-4 w-4 flex-shrink-0 text-mk-pink" />
                    {f}
                  </li>
                ))}
              </ul>
              {t.popular ? (
                <PinkButton href={t.href} className="w-full rounded-lg">
                  {t.cta}
                </PinkButton>
              ) : (
                <Link
                  href={t.href}
                  className="w-full rounded-lg border border-white/10 bg-white/[0.05] py-3 text-center font-mk-label text-xs font-bold uppercase tracking-[0.18em] text-mk-ink backdrop-blur-2xl transition-colors hover:bg-white/10"
                >
                  {t.cta}
                </Link>
              )}
            </GlassCard>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
